import { useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router';
import { ArrowUpToLineIcon, PlusIcon } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

import { API_BASE_URL } from '@/constants';
import { Post } from '@/components/Posts/Post';
import { NbButton, NbPageHeader } from '@/components/Neobrut';
import { useAuthToken } from '@/hooks/useAuthToken';
import { LostAndFoundSkeleton } from '@/components/Skeletons';

export const MyPublications = () => {
  const { token, isTokenExpired, userIdFromToken } = useAuthToken();
  const navigate = useNavigate();
  const titleRef = useRef<HTMLDivElement | null>(null);
  const {
    data: pets,
    isLoading,
    isError,
    error,
    refetch,
  } = useQuery({
    queryKey: ['my-publications'],
    queryFn: async (): Promise<Array<Pet>> => {
      if (!token || isTokenExpired(token)) {
        toast.info('Tu sesión ha expirado. Iniciá sesión nuevamente.');
        navigate('/login');
        return [];
      }
      const response = await axios.get(
        `${API_BASE_URL}/pets/?uploaded_by__id=${userIdFromToken(token)}`,
        { params: { no_pagination: true } },
      );
      return response.data;
    },
  });
  const [showScrollButton, setShowScrollButton] = useState(false);

  useEffect(() => {
    const handleTouchMove = (e: TouchEvent) => {
      setShowScrollButton(e.touches[0].clientY < window.innerHeight * 0.8);
    };
    window.addEventListener('touchmove', handleTouchMove, { passive: true });
    return () => window.removeEventListener('touchmove', handleTouchMove);
  }, []);

  const scrollToTop = () =>
    window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });

  if (isLoading) return <LostAndFoundSkeleton />;

  return (
    <div className="mx-auto w-full max-w-4xl lg:max-w-5xl pb-10">
      <div ref={titleRef}>
        <NbPageHeader
          slug="/ mis publicaciones"
          title={
            <>
              Tu historial,<br />
              <span className="nb-highlight">tuyo.</span>
            </>
          }
          fill="lost"
        />
      </div>

      {isError && <div className="px-4 py-16 text-center">Error: {error.message}</div>}

      {pets && pets.length === 0 ? (
        <div className="px-4 py-16 text-center">
          <p className="text-[14px] font-medium">No publicaste nada todavía.</p>
          <div className="mt-4">
            <NbButton fill="ink" onClick={() => navigate('/publish')} className="px-5 py-3">
              <PlusIcon size={14} /> Publicar mascota
            </NbButton>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-3 px-4 pt-4 sm:grid-cols-3 lg:grid-cols-4">
          {pets?.map((pet) => (
            <Post key={pet.id} pet={pet} refetch={refetch} />
          ))}
        </div>
      )}

      {showScrollButton && (
        <button
          onClick={scrollToTop}
          aria-label="Volver al inicio"
          className="nb-btn fill-urgent fixed bottom-20 right-3 z-20 px-2 py-2"
        >
          <ArrowUpToLineIcon color="black" />
        </button>
      )}
    </div>
  );
};
